import { useEffect, useMemo } from 'react';

import {
  AxiosErrorHandler,
  Col,
  Empty,
  Form,
  FormActions,
  Modal,
  NotificationOffLineIcon,
  Row,
  Skeleton,
  Title,
  message,
} from 'ui';
import { handleAxiosError } from 'common/error-handlers';
import { useNotificationSettingsQuery, useUpdateNotificationSettings } from '../../queries';
import { NotificationCheckbox } from './notification-checkbox';
import { isChangeAllowed } from './utilities';

export type CommunityNotificationSettingsFormValues = {
  emailNotificationsSettings?: {
    events?: boolean;
    giving?: boolean;
    newsfeed?: boolean;
    program?: boolean;
  };
  pushNotificationsSettings?: {
    events?: boolean;
    giving?: boolean;
    newsfeed?: boolean;
    program?: boolean;
  };
};

type P = {
  communityId: string;
  isOpened: boolean;
  onClose: () => void;
};

const NotificationSettingsModal = ({ communityId, onClose, ...rest }: P) => {
  const {
    data: notificationSettings,
    error: queryError,
    isLoading: isLoadingNotifications,
    isRefetching: isRefetchingNotifications,
  } = useNotificationSettingsQuery({ id: communityId });

  const {
    error: updateError,
    isPending: isUpdating,
    isSuccess: isUpdated,
    mutate: updateNotificationSettings,
  } = useUpdateNotificationSettings();

  useEffect(() => {
    if (isUpdated) {
      message.success('Notification settings was successfully updated.');
      onClose();
    }
  }, [onClose, isUpdated]);

  useEffect(() => {
    handleAxiosError(updateError);
  }, [updateError]);

  const initialValues: Partial<CommunityNotificationSettingsFormValues> | undefined = useMemo(() => {
    if (notificationSettings) {
      return {
        emailNotificationsSettings: {
          events: notificationSettings.emailNotificationsSettings.events.value,
          giving: notificationSettings.emailNotificationsSettings.giving.value,
          newsfeed: notificationSettings.emailNotificationsSettings.newsfeed.value,
          program: notificationSettings.emailNotificationsSettings.program.value,
        },
        pushNotificationsSettings: {
          events: notificationSettings.pushNotificationsSettings.events.value,
          giving: notificationSettings.pushNotificationsSettings.giving.value,
          newsfeed: notificationSettings.pushNotificationsSettings.newsfeed.value,
          program: notificationSettings.pushNotificationsSettings.program.value,
        },
      };
    }

    return {};
  }, [notificationSettings]);

  const isPossibleToEditSomeSettings = useMemo(() => {
    if (notificationSettings) {
      const notificationTypes = ['emailNotificationsSettings', 'pushNotificationsSettings'] as const;
      const notificationEntities = ['giving', 'events', 'newsfeed', 'program'] as const;

      return notificationTypes.some((notificationType) =>
        notificationEntities.some((notificationEntity) =>
          isChangeAllowed({
            notificationEntity,
            notificationSettings,
            notificationType,
          }),
        ),
      );
    }

    return false;
  }, [notificationSettings]);

  const handleSubmit = (values: CommunityNotificationSettingsFormValues) => {
    updateNotificationSettings({ id: communityId, values });
  };

  return (
    <Modal closable={!isUpdating} onCancel={onClose} title="Community Notification Settings" width="medium" {...rest}>
      <Modal.HugContent>
        <AxiosErrorHandler actions={false} error={queryError}>
          {isLoadingNotifications || isRefetchingNotifications || !notificationSettings ? (
            <Skeleton active paragraph={{ rows: 5 }} />
          ) : (
            <Form<CommunityNotificationSettingsFormValues>
              className="community-notification-settings"
              initialValues={initialValues}
              onFinish={handleSubmit}
            >
              {isPossibleToEditSomeSettings ? (
                <>
                  <Row>
                    <Col span={12}>
                      <Title level={5} marginBottom>
                        Email Notifications
                      </Title>
                      <NotificationCheckbox
                        label="Newsfeed"
                        notificationEntity="newsfeed"
                        notificationSettings={notificationSettings}
                        notificationType="emailNotificationsSettings"
                      />
                      <NotificationCheckbox
                        label="Events"
                        notificationEntity="events"
                        notificationSettings={notificationSettings}
                        notificationType="emailNotificationsSettings"
                      />
                      <NotificationCheckbox
                        label="Programs"
                        notificationEntity="program"
                        notificationSettings={notificationSettings}
                        notificationType="emailNotificationsSettings"
                      />
                      <NotificationCheckbox
                        label="Giving"
                        notificationEntity="giving"
                        notificationSettings={notificationSettings}
                        notificationType="emailNotificationsSettings"
                      />
                    </Col>
                    <Col span={12}>
                      <Title level={5} marginBottom>
                        Push Notifications
                      </Title>
                      <NotificationCheckbox
                        label="Newsfeed"
                        notificationEntity="newsfeed"
                        notificationSettings={notificationSettings}
                        notificationType="pushNotificationsSettings"
                      />
                      <NotificationCheckbox
                        label="Events"
                        notificationEntity="events"
                        notificationSettings={notificationSettings}
                        notificationType="pushNotificationsSettings"
                      />
                      <NotificationCheckbox
                        label="Programs"
                        notificationEntity="program"
                        notificationSettings={notificationSettings}
                        notificationType="pushNotificationsSettings"
                      />
                      <NotificationCheckbox
                        label="Giving"
                        notificationEntity="giving"
                        notificationSettings={notificationSettings}
                        notificationType="pushNotificationsSettings"
                      />
                    </Col>
                  </Row>

                  <Modal.HugContent.Footer>
                    <FormActions
                      right={{ children: 'Cancel', disabled: isUpdating, onClick: onClose }}
                      submit={{ children: 'Save', loading: isUpdating }}
                    />
                  </Modal.HugContent.Footer>
                </>
              ) : (
                <Row>
                  <Col span={24}>
                    <Empty
                      description={{
                        subtitle:
                          'Either notifications are disabled by default in this community, or your global notifications are turned off',
                        title: 'Notifications disabled',
                      }}
                      image={<NotificationOffLineIcon />}
                    />
                  </Col>
                </Row>
              )}
            </Form>
          )}
        </AxiosErrorHandler>
      </Modal.HugContent>
    </Modal>
  );
};

export default NotificationSettingsModal;
